import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export const BREACH_WAV_SRC = '/sounds/loud-alerts/breach.wav';
export const BREACH_PLAY_COUNT = 3;
export const ENVELOPE_REPEAT_COUNT = 2;
export const ENVELOPE_REPEAT_GAP_SECONDS = 0.12;
export const LOUD_WAV_VOLUME = 1;
export const NORMAL_WAV_VOLUME = 0.5;
export const LOUD_ENVELOPE_GAIN = 0.24;
export const NORMAL_ENVELOPE_GAIN = 0.12;

type EnvelopeTone = {
  frequencyHz: number;
  durationSeconds: number;
  startDelaySeconds?: number;
};

type AudioAlertsPlayerContextValue = {
  audioReady: boolean;
  audioUnlockFailed: boolean;
  audioUnlockPending: boolean;
  breachPlaying: boolean;
  resolveCuePlaying: boolean;
  unlockAudio: () => void;
  playBreach: (options: { loud: boolean }) => void;
  playPartialResolve: (options: { loud: boolean }) => void;
  playAllClear: (options: { loud: boolean }) => void;
  stopBreach: () => void;
  preloadBreachAudio: () => void;
};

const AudioAlertsPlayerContext = createContext<AudioAlertsPlayerContextValue | null>(null);

let audioContext: AudioContext | undefined;
let breachAudio: HTMLAudioElement | undefined;

const getAudioContext = (): AudioContext | undefined => {
  if (typeof window === 'undefined' || typeof AudioContext === 'undefined') return undefined;
  if (!audioContext) {
    audioContext = new AudioContext();
  }
  return audioContext;
};

const getBreachAudio = (): HTMLAudioElement | undefined => {
  if (typeof Audio === 'undefined') return undefined;
  if (!breachAudio) {
    breachAudio = new Audio(BREACH_WAV_SRC);
    breachAudio.preload = 'auto';
  }
  return breachAudio;
};

const resumeAudioAlertsAudioContext = () => {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    const resumeResult = ctx.resume();
    if (resumeResult !== undefined) {
      void resumeResult.catch(() => undefined);
    }
  } catch {
    return;
  }
};

const unlockAudioAlertsPlayback = (): Promise<void> => {
  try {
    resumeAudioAlertsAudioContext();
    if (typeof Audio === 'undefined') {
      return Promise.reject(new Error('Audio is unavailable'));
    }
    const probe = new Audio(BREACH_WAV_SRC);
    probe.muted = true;
    const playResult = probe.play();
    if (playResult === undefined) {
      probe.pause();
      probe.currentTime = 0;
      return Promise.resolve();
    }
    return playResult.then(() => {
      probe.pause();
      probe.currentTime = 0;
    });
  } catch (error) {
    return Promise.reject(error);
  }
};

const sequenceDurationSeconds = (tones: readonly EnvelopeTone[]) =>
  tones.reduce((latestEnd, tone) => {
    const start = tone.startDelaySeconds ?? 0;
    return Math.max(latestEnd, start + tone.durationSeconds);
  }, 0);

const envelopeDurationSeconds = (tones: readonly EnvelopeTone[]) => {
  const sequenceSeconds = sequenceDurationSeconds(tones);
  if (ENVELOPE_REPEAT_COUNT <= 1) return sequenceSeconds;
  return (
    ENVELOPE_REPEAT_COUNT * sequenceSeconds +
    (ENVELOPE_REPEAT_COUNT - 1) * ENVELOPE_REPEAT_GAP_SECONDS
  );
};

export const AudioAlertsProvider = ({ children }: { children: ReactNode }) => {
  const [audioReady, setReady] = useState(false);
  const [audioUnlockFailed, setUnlockFailed] = useState(false);
  const [audioUnlockPending, setUnlockPending] = useState(false);
  const [breachPlaying, setBreachPlaying] = useState(false);
  const [resolveCuePlaying, setResolveCuePlaying] = useState(false);
  const mountedRef = useRef(true);
  const audioReadyRef = useRef(false);
  const breachPlayingRef = useRef(false);
  const resolveCuePlayingRef = useRef(false);
  const remainingBreachPlaysRef = useRef(0);
  const onBreachEndedRef = useRef<(() => void) | undefined>(undefined);
  const resolveCueTimeoutIdRef = useRef<number | undefined>(undefined);
  const resolveOscillatorsRef = useRef<OscillatorNode[]>([]);

  const syncBreachPlaying = useCallback((playing: boolean) => {
    if (breachPlayingRef.current === playing) return;
    breachPlayingRef.current = playing;
    if (mountedRef.current) {
      setBreachPlaying(playing);
    }
  }, []);

  const syncResolveCuePlaying = useCallback((playing: boolean) => {
    if (resolveCuePlayingRef.current === playing) return;
    resolveCuePlayingRef.current = playing;
    if (mountedRef.current) {
      setResolveCuePlaying(playing);
    }
  }, []);

  const markAudioReady = useCallback(() => {
    audioReadyRef.current = true;
    if (!mountedRef.current) return;
    setReady(true);
    setUnlockFailed(false);
    setUnlockPending(false);
  }, []);

  const haltBreachPlayback = useCallback(() => {
    const onEnded = onBreachEndedRef.current;
    if (breachAudio && onEnded) {
      breachAudio.removeEventListener('ended', onEnded);
      onBreachEndedRef.current = undefined;
    }
    remainingBreachPlaysRef.current = 0;
    if (!breachAudio) return;
    breachAudio.pause();
    breachAudio.currentTime = 0;
  }, []);

  const stopResolveCue = useCallback(() => {
    if (resolveCueTimeoutIdRef.current !== undefined) {
      window.clearTimeout(resolveCueTimeoutIdRef.current);
      resolveCueTimeoutIdRef.current = undefined;
    }
    resolveOscillatorsRef.current.forEach(oscillator => {
      try {
        oscillator.stop();
      } catch {
        return;
      }
    });
    resolveOscillatorsRef.current = [];
    syncResolveCuePlaying(false);
  }, [syncResolveCuePlaying]);

  const failBreachPlay = useCallback(() => {
    haltBreachPlayback();
    syncBreachPlaying(false);
  }, [haltBreachPlayback, syncBreachPlaying]);

  const startBreachPlayback = useCallback(
    (audio: HTMLAudioElement) => {
      audio.pause();
      audio.currentTime = 0;
      const playResult = audio.play();
      if (playResult === undefined) {
        if (!audioReadyRef.current) {
          markAudioReady();
        }
        return;
      }
      if (audioReadyRef.current) {
        void playResult.catch(failBreachPlay);
        return;
      }
      void playResult.then(markAudioReady, failBreachPlay);
    },
    [failBreachPlay, markAudioReady],
  );

  const playEnvelopeTone = useCallback(
    ({
      frequencyHz,
      durationSeconds,
      startDelaySeconds = 0,
      loud,
    }: EnvelopeTone & { loud: boolean }) => {
      const ctx = getAudioContext();
      if (!ctx) return;
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.value = frequencyHz;
      const startAt = ctx.currentTime + startDelaySeconds;
      const attackSeconds = 0.02;
      const stopAt = startAt + durationSeconds;
      const peakGain = loud ? LOUD_ENVELOPE_GAIN : NORMAL_ENVELOPE_GAIN;
      gain.gain.setValueAtTime(0.0001, startAt);
      gain.gain.exponentialRampToValueAtTime(peakGain, startAt + attackSeconds);
      gain.gain.exponentialRampToValueAtTime(0.0001, stopAt);
      oscillator.connect(gain);
      gain.connect(ctx.destination);
      oscillator.start(startAt);
      oscillator.stop(stopAt);
      resolveOscillatorsRef.current = [...resolveOscillatorsRef.current, oscillator];
    },
    [],
  );

  const playEnvelopeSequence = useCallback(
    ({ tones, loud }: { tones: readonly EnvelopeTone[]; loud: boolean }) => {
      if (breachPlayingRef.current || resolveCuePlayingRef.current) return;
      const ctx = getAudioContext();
      if (!ctx) return;
      if (!audioReadyRef.current && ctx.state === 'running') {
        markAudioReady();
      }
      for (let repeatIndex = 0; repeatIndex < ENVELOPE_REPEAT_COUNT; repeatIndex += 1) {
        const sequenceOffsetSeconds =
          repeatIndex * (sequenceDurationSeconds(tones) + ENVELOPE_REPEAT_GAP_SECONDS);
        for (const tone of tones) {
          playEnvelopeTone({
            ...tone,
            startDelaySeconds: (tone.startDelaySeconds ?? 0) + sequenceOffsetSeconds,
            loud,
          });
        }
      }
      syncResolveCuePlaying(true);
      if (typeof window === 'undefined') {
        syncResolveCuePlaying(false);
        return;
      }
      resolveCueTimeoutIdRef.current = window.setTimeout(() => {
        resolveCueTimeoutIdRef.current = undefined;
        resolveOscillatorsRef.current = [];
        syncResolveCuePlaying(false);
      }, envelopeDurationSeconds(tones) * 1000);
    },
    [markAudioReady, playEnvelopeTone, syncResolveCuePlaying],
  );

  const playBreach = useCallback(
    ({ loud }: { loud: boolean }) => {
      const audio = getBreachAudio();
      if (!audio) return;
      stopResolveCue();
      haltBreachPlayback();
      audio.volume = loud ? LOUD_WAV_VOLUME : NORMAL_WAV_VOLUME;
      remainingBreachPlaysRef.current = BREACH_PLAY_COUNT;
      const onEnded = () => {
        remainingBreachPlaysRef.current -= 1;
        if (remainingBreachPlaysRef.current <= 0) {
          if (onBreachEndedRef.current) {
            audio.removeEventListener('ended', onBreachEndedRef.current);
            onBreachEndedRef.current = undefined;
          }
          syncBreachPlaying(false);
          return;
        }
        startBreachPlayback(audio);
      };
      onBreachEndedRef.current = onEnded;
      audio.addEventListener('ended', onEnded);
      startBreachPlayback(audio);
      syncBreachPlaying(true);
    },
    [haltBreachPlayback, startBreachPlayback, stopResolveCue, syncBreachPlaying],
  );

  const stopBreach = useCallback(() => {
    haltBreachPlayback();
    syncBreachPlaying(false);
  }, [haltBreachPlayback, syncBreachPlaying]);

  const playPartialResolve = useCallback(
    ({ loud }: { loud: boolean }) => {
      playEnvelopeSequence({
        loud,
        tones: [{ frequencyHz: 880, durationSeconds: 0.18 }],
      });
    },
    [playEnvelopeSequence],
  );

  const playAllClear = useCallback(
    ({ loud }: { loud: boolean }) => {
      playEnvelopeSequence({
        loud,
        tones: [
          { frequencyHz: 523.25, durationSeconds: 0.16 },
          { frequencyHz: 784, durationSeconds: 0.28, startDelaySeconds: 0.14 },
        ],
      });
    },
    [playEnvelopeSequence],
  );

  const preloadBreachAudio = useCallback(() => {
    getBreachAudio()?.load();
  }, []);

  const unlockAudio = useCallback(() => {
    setUnlockPending(true);
    setUnlockFailed(false);
    void unlockAudioAlertsPlayback()
      .then(() => {
        audioReadyRef.current = true;
        if (!mountedRef.current) return;
        setReady(true);
        setUnlockFailed(false);
        setUnlockPending(false);
      })
      .catch(() => {
        audioReadyRef.current = false;
        if (!mountedRef.current) return;
        setReady(false);
        setUnlockFailed(true);
        setUnlockPending(false);
      });
  }, []);

  useEffect(function resetCuesOnUnmount() {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (resolveCueTimeoutIdRef.current !== undefined) {
        window.clearTimeout(resolveCueTimeoutIdRef.current);
        resolveCueTimeoutIdRef.current = undefined;
      }
      resolveOscillatorsRef.current.forEach(oscillator => {
        try {
          oscillator.stop();
        } catch {
          return;
        }
      });
      resolveOscillatorsRef.current = [];
      resolveCuePlayingRef.current = false;
      const onEnded = onBreachEndedRef.current;
      if (breachAudio && onEnded) {
        breachAudio.removeEventListener('ended', onEnded);
        onBreachEndedRef.current = undefined;
      }
      remainingBreachPlaysRef.current = 0;
      breachPlayingRef.current = false;
    };
  }, []);

  const value = useMemo(
    () => ({
      audioReady,
      audioUnlockFailed,
      audioUnlockPending,
      breachPlaying,
      resolveCuePlaying,
      unlockAudio,
      playBreach,
      playPartialResolve,
      playAllClear,
      stopBreach,
      preloadBreachAudio,
    }),
    [
      audioReady,
      audioUnlockFailed,
      audioUnlockPending,
      breachPlaying,
      playAllClear,
      playBreach,
      playPartialResolve,
      preloadBreachAudio,
      resolveCuePlaying,
      stopBreach,
      unlockAudio,
    ],
  );

  return (
    <AudioAlertsPlayerContext.Provider value={value}>{children}</AudioAlertsPlayerContext.Provider>
  );
};

export const useAudioAlertsPlayer = () => {
  const ctx = useContext(AudioAlertsPlayerContext);
  if (!ctx) {
    throw new Error('useAudioAlertsPlayer must be used within an AudioAlertsProvider');
  }
  return ctx;
};
